import { camelCase, get } from "lodash";

import { DOMAIN, STATE } from "../../thor.const";
import { Enums } from "../../thor.enums";
import { SaleSearchGet } from "../../typings/search-api";

export class LokiSalesController {
    public static $inject: string[] = [
        "$mdDialog",
        "$q",
        "$scope",
        "$state",
        "$translate",
        "$window",
        "configService",
        "liveService",
        "lokiFilterService",
        "onlineDateService",
        "organizationService",
        "sitesService",
        "toaster"
    ];

    public context: string;
    public sales: SaleSearchGet[] = [];
    public salesTotal: number;
    public hasMoreSales: boolean;

    private $t;

    constructor(
        private $mdDialog: ng.material.IDialogService,
        protected $q: ng.IQService,
        protected $scope: ng.IScope,
        protected $state: ng.ui.IStateService,
        $translate: ng.translate.ITranslateService,
        protected $window: ng.IWindowService,
        protected configService,
        protected liveService,
        protected lokiFilterService,
        private onlineDateService,
        private organizationService,
        public sitesService,
        private toaster
    ) {
        this.$t = $translate.instant;
    }

    public $onInit() {
        this.context = this.$state.current.data.context;
        const contextKey = camelCase(`${this.context}-sales`);

        this.$scope.$watchCollection(
            () => this.lokiFilterService[contextKey],
            () => {
                this.sales = this.lokiFilterService[contextKey]?.items;
                this.salesTotal = this.lokiFilterService[contextKey]?.total;
                this.hasMoreSales = this.lokiFilterService[contextKey]?.moreResults;
            }
        );
    }

    public isOnlineScheduled(sale: SaleSearchGet): boolean {
        const { saleScheduled } = this.onlineDateService.getSaleStatus(sale);
        return this.context === "future" && this.isSaleOnline(sale) && saleScheduled;
    }

    public openOrganization(organizationId) {
        const organizationUrl = this.$state.href(STATE.V2.HOME.name, {}, { absolute: true });

        this.goAsOrganization(organizationUrl, organizationId);
    }

    public openSale(organizationId, sale: SaleSearchGet, openItemsTab: boolean = false) {
        const isOnline: boolean = this.isSaleOnline(sale);
        const isPast = this.context === "past";
        const configEnableHeader = this.configService.enableV2?.header || {};
        const getSaleUrl = () => {
            if (
                (isOnline && !isPast && !/true/.test(configEnableHeader.futureSaleOnline)) ||
                (isOnline && isPast && !/true/.test(configEnableHeader.pastSaleOnline)) ||
                (!isOnline && !isPast && !/true/.test(configEnableHeader.futureSaleLiveClassic)) ||
                (!isOnline && isPast && !/true/.test(configEnableHeader.pastSaleLiveClassic))
            ) {
                return this.$state.href(
                    `thor.${isOnline ? "online-container" : "sale"}.${openItemsTab ? "items" : "information"}`,
                    { id: sale.id },
                    { absolute: true }
                );
            }
            return openItemsTab ? STATE.V2.SALES.SALE.ITEMS.url(sale.id) : STATE.V2.SALES.SALE.INFO.url(sale.id);
        };
        const saleUrl = getSaleUrl();

        return this.goAsOrganization(saleUrl, organizationId);
    }

    public setOffered(sale: SaleSearchGet) {
        const offered = sale.live?.offered;
        const reset = () => (sale.live.offered = !sale.live?.offered);
        const confirm = this.$mdDialog
            .confirm()
            .title(this.$t("sale_offered"))
            .textContent(
                this.$t(
                    offered
                        ? "etes_vous_sur_de_vouloir_faire_une_remise_sur_cette_vente"
                        : "souhaitez_vous_annuler_la_remise_sur_cette_vente"
                )
            )
            .ok(this.$t("valider"))
            .cancel(this.$t("annuler"));

        this.$mdDialog
            .show(confirm)
            .then(() =>
                this.liveService.setOffered(sale.id, sale.organization.id, { offered }).catch(() => {
                    reset();
                    this.toaster.show(this.$t("une_erreur_est_survenue"), true);
                })
            )
            .catch(() => reset());
    }

    private goAsOrganization(url: string, oId: number) {
        return this.$q((resolve, reject) => {
            this.organizationService.getOrganization(oId)
                .then(organization => {
                    const isOrganizationAtlasIe = this.sitesService.organizationCanPublishToInterencheres(organization);
                    const host = this.$window.location.host;
                    const isLocalhost = (/localhost/.exec(host) || { index: -1 }).index === 0;

                    if (!isLocalhost) {
                        const {groups: { env }}: any = /atlas\.(?<env>dev|int|pp)-/.exec(host) || {groups: {}};
                        const {
                            [Enums.Site.Auction]: auctionDomain,
                            [Enums.Site.Interencheres]: ieDomain,
                            indbDomain = "indb.io"
                        }: any = DOMAIN;

                        url = isOrganizationAtlasIe ?
                            url.replace(new RegExp(auctionDomain), env ? indbDomain : ieDomain) :
                            url.replace(new RegExp(`${indbDomain}|${ieDomain}`), auctionDomain);
                    }
                    resolve(this.$window.open(`${url}?oId=${oId}`, "_blank"));
                })
                .catch(() => reject());
        });
    }

    private isSaleOnline(sale: SaleSearchGet): boolean {
        return get(sale, "sites.interencheres.type") === Enums.AuctionType.Online;
    }
}
